import { useCallback, useMemo } from "react"

import {
  createSimulationWrapperInstance,
  SimulationWrapperType,
  type SimulationWrapperConfig,
} from "@/test-set/simulation-wrappers"

import { useTestSetEditorContext } from "../contexts/TestSetEditorContext"
import { EVENTS, useEventDispatcher } from "../event-bus"
import { getTestSetSimulationWrapperConfig } from "../utils/testSetSimulationWrapperConfig"

export function useTestSetSimulationWrapperConfig() {
  const dispatch = useEventDispatcher()
  const { featureFlags, metadata, testCases } = useTestSetEditorContext()

  const handleSimulationWrapperChange = useCallback(
    (type: SimulationWrapperType | null) => {
      dispatch(EVENTS.METADATA_UPDATE, {
        simulationWrapperType: type,
        simulationWrapperTopLevelConfig:
          type === metadata.simulationWrapperType
            ? metadata.simulationWrapperTopLevelConfig
            : getTestSetSimulationWrapperConfig(type, testCases),
      })
    },
    [dispatch, metadata, testCases],
  )

  const handleSimulationWrapperConfigChange = useCallback(
    (config: SimulationWrapperConfig) => {
      dispatch(EVENTS.METADATA_UPDATE, {
        simulationWrapperTopLevelConfig: config,
      })
    },
    [dispatch],
  )

  const simulationWrapper = useMemo(() => {
    if (!metadata.simulationWrapperType) {
      return null
    }
    return createSimulationWrapperInstance(metadata.simulationWrapperType)
  }, [metadata.simulationWrapperType])

  const hasEnabledSimulationWrapper =
    featureFlags.isCustomWrapperObservationsEnabled ||
    featureFlags.isApiSimulationWrapperEnabled
  const isSelectedSimulationWrapperEnabled =
    (metadata.simulationWrapperType === SimulationWrapperType.CUSTOM &&
      featureFlags.isCustomWrapperObservationsEnabled) ||
    (metadata.simulationWrapperType === SimulationWrapperType.API_WRAPPER &&
      featureFlags.isApiSimulationWrapperEnabled)

  return {
    hasEnabledSimulationWrapper,
    handleSimulationWrapperChange,
    handleSimulationWrapperConfigChange,
    isSelectedSimulationWrapperEnabled,
    simulationWrapper,
  }
}
